import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useApi } from 'api/api-context';
import BookingAccountLayout from 'pages/booking/account/BookingAccountLayout';
import {
  AccountError,
  AccountSkeleton,
  BookingInterval,
  PeriodicBookingBadge,
  ReservationStatusBadge,
} from 'pages/booking/account/account-utils';
import { BOOKING_ACCOUNT_BOOKING_DETAIL_PATH } from 'routes/paths';
import useLang from 'lang';
import AccountFilters from 'pages/booking/account/AccountFilters';

type BookingView = 'upcoming' | 'past' | 'cancelled';

const STATUS_OPTIONS: Record<BookingView, string[]> = {
  upcoming: ['created', 'accepted', 'waiting-for-payment'],
  past: ['created', 'accepted', 'fulfilled'],
  cancelled: ['cancelled', 'payment-failed', 'payment-review-required'],
};

/**
 * Paginated upcoming, past and unsuccessful reservation history.
 */
export default function AccountBookings() {
  const { getAction } = useApi();
  const { _t } = useLang();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedView = (searchParams.get('view') as BookingView) || 'upcoming';
  const [rows, setRows] = useState<any[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [filters, setFilters] = useState({ buildingId: '', spaceId: '', status: '', from: '', to: '' });

  const load = useCallback(
    async (append = false) => {
      setLoading(true);
      setError(false);
      try {
        const data = (await getAction('GetReservations')({
          query: {
            view: selectedView,
            limit: 20,
            ...(append && nextCursor ? { cursor: nextCursor } : {}),
            ...Object.fromEntries(Object.entries(filters).filter(([, value]) => value)),
          } as any,
        })) as any;
        setRows((current) => (append ? [...current, ...(data.reservations || [])] : data.reservations || []));
        setNextCursor(data.nextCursor || null);
      } catch (_error) {
        setError(true);
      } finally {
        setLoading(false);
      }
    },
    [getAction, selectedView, filters, nextCursor]
  );

  useEffect(() => {
    setRows([]);
    setNextCursor(null);
    void load(false);
  }, [selectedView, filters]);

  const options = useMemo(() => {
    const buildings = new Map(rows.map((row) => [row.building?.id, row.building?.title]));
    const spaces = new Map(rows.map((row) => [row.space?.id, row.space?.name]));
    return { buildings, spaces };
  }, [rows]);

  /**
   * Changes the active booking classification while keeping the URL shareable.
   */
  const changeView = (view: BookingView) => {
    setSearchParams({ view });
  };

  return (
    <BookingAccountLayout>
      <div className="account-subtabs">
        {(['upcoming', 'past', 'cancelled'] as BookingView[]).map((view) => (
          <button className={selectedView === view ? 'active' : ''} key={view} onClick={() => changeView(view)}>
            {_t(view === 'upcoming' ? 'Upcoming' : view === 'past' ? 'Past' : 'Cancelled')}
          </button>
        ))}
      </div>
      <AccountFilters activeFilterCount={Object.values(filters).filter(Boolean).length}>
        <select
          aria-label={_t('Building')}
          value={filters.buildingId}
          onChange={(event) => setFilters((current) => ({ ...current, buildingId: event.target.value }))}
        >
          <option value="">{_t('All buildings')}</option>
          {[...options.buildings].map(([id, label]) => (
            <option value={id} key={id}>
              {label}
            </option>
          ))}
        </select>
        <select
          aria-label={_t('Space')}
          value={filters.spaceId}
          onChange={(event) => setFilters((current) => ({ ...current, spaceId: event.target.value }))}
        >
          <option value="">{_t('All spaces')}</option>
          {[...options.spaces].map(([id, label]) => (
            <option value={id} key={id}>
              {label}
            </option>
          ))}
        </select>
        <select
          aria-label={_t('Status')}
          value={filters.status}
          onChange={(event) => setFilters((current) => ({ ...current, status: event.target.value }))}
        >
          <option value="">{_t('All statuses')}</option>
          {STATUS_OPTIONS[selectedView].map((status) => (
            <option value={status} key={status}>
              {_t(
                status === 'created'
                  ? 'Awaiting approval'
                  : status === 'waiting-for-payment'
                    ? 'Waiting for Payment'
                    : status === 'payment-failed'
                      ? 'Payment failed'
                      : status === 'payment-review-required'
                        ? 'Payment requires review'
                        : status.charAt(0).toUpperCase() + status.slice(1)
              )}
            </option>
          ))}
        </select>
        <label className="account-filter-field">
          <span>{_t('From')}:</span>
          <input
            aria-label={_t('From')}
            type="date"
            value={filters.from}
            onChange={(event) => setFilters((current) => ({ ...current, from: event.target.value }))}
          />
        </label>
        <label className="account-filter-field">
          <span>{_t('To')}:</span>
          <input
            aria-label={_t('To')}
            type="date"
            value={filters.to}
            onChange={(event) => setFilters((current) => ({ ...current, to: event.target.value }))}
          />
        </label>
      </AccountFilters>
      {loading && rows.length === 0 && <AccountSkeleton />}
      {error && <AccountError retry={() => void load(false)} />}
      {!loading && !error && rows.length === 0 && (
        <div className="account-state">
          <h2>{_t('No bookings here yet')}</h2>
          <p>{_t('Your reservations will appear here.')}</p>
        </div>
      )}
      <div className="account-card-list">
        {rows.map((reservation) => (
          <article className="account-booking-card" key={reservation.id}>
            {reservation.space?.imageUrl && <img src={reservation.space.imageUrl} alt="" />}
            <div>
              <div className="account-card-heading">
                <div>
                  <h2>{reservation.space?.name}</h2>
                  <p>{reservation.building?.title}</p>
                </div>
                <ReservationStatusBadge status={reservation.status} />
              </div>
              <p className="account-booking-time">
                <BookingInterval startsAt={reservation.startsAt} endsAt={reservation.endsAt} />
              </p>
              <PeriodicBookingBadge periodic={reservation.periodic} />
              <button
                className="account-button account-button--secondary"
                onClick={() =>
                  navigate(BOOKING_ACCOUNT_BOOKING_DETAIL_PATH.insert({ reservationId: String(reservation.id) }))
                }
              >
                {_t('View details')}
              </button>
            </div>
          </article>
        ))}
      </div>
      {nextCursor && (
        <button className="account-button account-load-more" disabled={loading} onClick={() => void load(true)}>
          {loading ? _t('Loading...') : _t('Load more')}
        </button>
      )}
    </BookingAccountLayout>
  );
}
